// ReviewCommentButton.tsx — the clinician comment affordance for Review Mode.
//
// Mounted by content viewers (a step) and the chapter celebration (a chapter).
// Renders NOTHING unless `reviewMode` is on, so end users never see it. It floats
// at the screen edge (out of the way of the viewer's own controls) and opens a
// bottom-sheet composer: the notes already on this piece of content, plus a box to
// add another. Notes are keyed by the target id so the export maps back to the
// manifest. This is an authoring aid only — it never changes the content.
import React from 'react';
import { Body, Meta, Btn, Pressable } from '@/components/ui';
import { Icon } from '@/components/icons';
import { useProgress } from '@/hooks/useProgress';

export function ReviewCommentButton({
  targetId,
  kind,
  label,
}: {
  targetId: string;
  kind: 'step' | 'chapter';
  /** A short human label for the composer header (e.g. the step or chapter title). */
  label?: string;
}) {
  const progress = useProgress();
  const [open, setOpen] = React.useState(false);
  const [draft, setDraft] = React.useState('');

  if (!progress.state.reviewMode) return null;

  const mine = progress.state.reviewComments.filter((c) => c.targetId === targetId);

  const add = () => {
    const t = draft.trim();
    if (!t) return;
    progress.addReviewComment({ targetId, kind, text: t });
    setDraft('');
  };

  return (
    <>
      {/* floating launcher — right edge, mid-height; shows a count when notes exist */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={mine.length ? `Review notes (${mine.length})` : 'Add a review note'}
        style={{
          position: 'fixed',
          right: 12,
          top: '46%',
          zIndex: 96,
          width: 46,
          height: 46,
          borderRadius: 999,
          border: '1px solid rgba(0,0,0,0.12)',
          background: '#3b2f6e',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          boxShadow: '0 10px 24px -10px rgba(40,30,90,0.7)',
        }}
      >
        <Icon name="pencil" size={20} />
        {mine.length > 0 && (
          <span
            style={{
              position: 'absolute',
              top: -3,
              right: -3,
              minWidth: 18,
              height: 18,
              padding: '0 4px',
              borderRadius: 999,
              background: '#d98a2b',
              color: '#fff',
              fontFamily: 'Inter, system-ui, sans-serif',
              fontSize: 11,
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {mine.length}
          </span>
        )}
      </button>

      {open && (
        <div
          onClick={() => setOpen(false)}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 98,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-end',
            background: 'rgba(28,22,16,0.4)',
            animation: 'fade .2s ease',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'var(--cream)',
              borderTopLeftRadius: 22,
              borderTopRightRadius: 22,
              padding: '20px 22px calc(22px + var(--safe-bottom))',
              maxHeight: '80vh',
              overflowY: 'auto',
              boxShadow: '0 -12px 40px -12px rgba(0,0,0,0.35)',
              animation: 'rise .3s cubic-bezier(.2,.7,.3,1)',
            }}
          >
            <div style={{ width: 38, height: 5, borderRadius: 999, background: 'var(--hair-strong)', margin: '0 auto 14px' }} />
            {/* Actions pinned at the TOP so they stay reachable above the on-screen
                keyboard (which the auto-focused textarea opens). */}
            <div style={{ position: 'sticky', top: 0, zIndex: 1, display: 'flex', gap: 10, background: 'var(--cream)', paddingBottom: 12, marginBottom: 2 }}>
              <Btn variant="ghost" onClick={() => setOpen(false)} style={{ flex: 1 }}>
                Done
              </Btn>
              <Btn onClick={add} style={{ flex: 1, opacity: draft.trim() ? 1 : 0.5 }}>
                Add note
              </Btn>
            </div>
            <div
              style={{
                fontFamily: 'Inter, system-ui, sans-serif',
                fontSize: 10.5,
                fontWeight: 700,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                color: '#6a5cb0',
                marginBottom: 4,
              }}
            >
              Review note · {kind}
            </div>
            <div style={{ fontFamily: 'Fraunces, Georgia, serif', fontSize: 18, color: 'var(--ink)', marginBottom: 4 }}>
              {label || targetId}
            </div>
            <Meta style={{ display: 'block', marginBottom: 14 }}>
              <code>{targetId}</code>
            </Meta>

            {/* existing notes on this target */}
            {mine.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 9, marginBottom: 14 }}>
                {mine.map((c) => (
                  <div
                    key={c.id}
                    style={{
                      padding: '11px 13px',
                      borderRadius: 13,
                      background: 'rgba(59,47,110,0.06)',
                      border: '1px solid rgba(59,47,110,0.16)',
                    }}
                  >
                    <Body size={14} style={{ whiteSpace: 'pre-wrap' }}>{c.text}</Body>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 7 }}>
                      <Meta>{c.updatedAt.slice(0, 10)}</Meta>
                      <Pressable
                        as="button"
                        onClick={() => progress.deleteReviewComment(c.id)}
                        ariaLabel="Delete note"
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(156,61,44,0.9)', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 12.5, fontWeight: 600, padding: '4px 2px' }}
                      >
                        Delete
                      </Pressable>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="What needs changing here? (wording, tone, an edit, a question…)"
              autoFocus
              style={{
                width: '100%',
                minHeight: 96,
                boxSizing: 'border-box',
                resize: 'none',
                borderRadius: 14,
                border: '1px solid var(--hair-strong)',
                background: '#fff',
                padding: 13,
                color: 'var(--ink)',
                fontFamily: 'Inter, system-ui, sans-serif',
                fontSize: 15.5,
                lineHeight: 1.5,
                outline: 'none',
              }}
            />
          </div>
        </div>
      )}
    </>
  );
}
